import { Comment, FullAuthored, FullAuthoredComment, FullComment, Post, zFullComment } from 'model';
import { flattenTree } from '../util/flattenTree';
import { BaseApi } from './BaseAPI';

const baseURL = 'comment/';
export const CommentApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		getAllCommentsForPost: builder.query<FullComment[], Post['_id'] | undefined>({
			query: id => ({
				url: `${baseURL}post/${id}?includeChildren=true`
			}),
			providesTags: result => {
				const t: { type: 'Comment'; id?: string | undefined }[] = flattenTree(result ?? []).map(comment => ({
					type: 'Comment',
					id: comment._id
				}));
				// If no comments are returned it would otherwise provide no tags, here we add a generic tag to ensure the query is refetched
				// even if no comments are returned
				if (t.length === 0) {
					t.push({ type: 'Comment' });
				}
				return t;
			}
		}),

		getComment: builder.query<FullAuthored<Comment>, Comment['_id']>({
			query: id => ({
				url: `${baseURL}${id}`
			}),
			providesTags: (result, error, arg) => [{ type: 'Comment', id: arg }]
		}),

		// This will get event comments that have been deleted
		getCommentAdmin: builder.query<FullAuthored<Comment>, Comment['_id']>({
			query: id => ({
				url: `${baseURL}admin/${id}`
			}),
			providesTags: (result, error, arg) => [{ type: 'Comment', id: arg }]
		}),

		getCommentsForPost: builder.query<Comment[], Comment['_id']>({
			query: id => ({
				url: `${baseURL}post/${id}?includeChildren=false`
			}),
			providesTags: result => result?.map(comment => ({ type: 'Comment', id: comment._id })) ?? []
		}),

		createComment: builder.mutation<FullAuthored<Comment>, { comment: Comment }>({
			query: ({ comment }) => ({
				url: `${baseURL}create`,
				method: 'POST',
				body: { comment }
			}),
			invalidatesTags: result => {
				return ['Comment', { type: 'Post', id: result?.root }];
			}
		}),

		rateComment: builder.mutation<FullComment, { comment: FullComment; like: boolean; dislike: boolean }>({
			query: ({ comment, like, dislike }) => ({
				url: `${baseURL}rate`,
				method: 'POST',
				body: { comment, like, dislike }
			}),
			invalidatesTags: (...args) => [
				{ type: 'Post', id: args[2].comment.root },
				{ type: 'Comment', id: args[2].comment._id }
			]
		}),

		updateComment: builder.mutation<FullComment, { comment: FullComment }>({
			query: ({ comment }) => ({
				url: `${baseURL}update`,
				method: 'POST',
				body: { comment }
			}),
			// Merge the new comment into the existing one to make a FullComment,
			// Update doesn't accept changes to children or the author, and won't return fully
			// populated children or author
			transformResponse: (response: FullAuthoredComment, meta, arg) => {
				const merged = zFullComment.parse({
					...response,
					children: arg.comment.children,
					fullChildren: arg.comment.fullChildren,
					author: arg.comment.author,
					feedback: 0
				});
				return merged;
			},
			invalidatesTags: (...args) => [{ type: 'Comment', id: args[2].comment._id }]
		}),

		deleteComment: builder.mutation<FullComment, Comment['_id']>({
			query: id => ({
				url: `${baseURL}${id}/`,
				method: 'DELETE'
			}),
			invalidatesTags: () => [{ type: 'Post' }, { type: 'Comment' }]
		}),

		moderateComment: builder.mutation<FullComment, { comment: FullComment }>({
			query: ({ comment }) => ({
				url: `${baseURL}moderate`,
				method: 'PUT',
				body: { comment }
			}),
			// Merge the new comment into the existing one to make a FullComment,
			// moderate does return a partial fullcomment, but doesn't return children.
			transformResponse: (response: FullAuthoredComment, meta, arg) => {
				const merged = zFullComment.parse({
					...response,
					children: arg.comment.children,
					fullChildren: arg.comment.fullChildren,
					author: arg.comment.author,
					feedback: 0
				});
				return merged;
			},
			invalidatesTags: (...args) => [{ type: 'Comment', id: args[2].comment._id }]
		}),
		// This marks a comment as deleted, but does not delete it.
		setDeletedComment: builder.mutation<FullComment, { id: Comment['_id']; deleted: boolean }>({
			query: ({ id, deleted }) => ({
				url: `${baseURL}setDeleted/${id}/${deleted}`,
				method: 'POST'
			}),
			invalidatesTags: () => [{ type: 'Post' }, { type: 'Comment' }]
		}),

		getCommunityQuestionStars: builder.query<Record<string, number>, { questionId: string }>({
			query: ({ questionId }) => ({
				url: `${baseURL}communityQuestionStars/${questionId}`,
				method: 'GET'
			}),
			providesTags: (result, error, { questionId }) => [{ type: 'Post', id: questionId }]
		})
	})
});

export const {
	useGetAllCommentsForPostQuery,
	useGetCommentsForPostQuery,
	useCreateCommentMutation,
	useRateCommentMutation,
	useUpdateCommentMutation,
	useGetCommentQuery,
	useDeleteCommentMutation,
	useLazyGetCommentQuery,
	useSetDeletedCommentMutation,
	useGetCommentAdminQuery,
	useLazyGetCommentAdminQuery,
	useModerateCommentMutation,
	useGetCommunityQuestionStarsQuery
} = CommentApi;
